import { call, put, select } from 'redux-saga/effects';

import { resetPartnerPatient, updatePatient } from 'connect/actions';
import { extractErrorMessage, sendToLogger } from 'connect/saga/helpers';
import { getPatient } from 'connect/saga/saga';
import { selectDataPartnerPatient, selectDataUser, selectDataUserAuthenticated } from 'connect/selectors';
import { apiUpdatePatientForPartner } from 'endpoints/partner-endpoints';
import { apiUpdatePatient } from 'endpoints/patient-endpoints';

import Logger from 'utils/Logger';

import { SagaHandler } from '../SagaHandler';

class _UpdatePatientDetails extends SagaHandler {
  constructor() {
    super({
      actionName: 'data/UPDATE_PATIENT_DETAILS',
      stateKey: 'updatePatientDetails',
    });
  }

  createAction({ patientDetails, patientId = null, autoVerify = false }) {
    return {
      type: this._actionName,
      patientDetails,
      patientId,
      autoVerify,
    };
  }

  * #validateCurrentPatient({ patientDetails, patientId, checkLoginStatus }) {
    const isAuthenticatedUser = yield select(selectDataUserAuthenticated);

    if (checkLoginStatus && !isAuthenticatedUser) {
      Logger.error("Tried to update patient details for user that isn't logged in", { patientDetails });
      return;
    }

    const patient = yield call(getPatient);

    if (!patient?.id && !patientId) {
      Logger.error("Tried to update patient details for patient that doesn't exist", { patientDetails });
    }

    return patient;
  }

  * #updatePartnerPatient({ patientDetails, patientId, currentUser }) {
    const partnerPatient = yield select(selectDataPartnerPatient);

    const updatedPatient = yield call(
      apiUpdatePatientForPartner,
      currentUser?.partner?.id,
      patientId || partnerPatient?.id,
      patientDetails,
    );
    yield put(resetPartnerPatient(updatedPatient));

    return updatedPatient;
  }

  * #updateCurrentPatient({ patientDetails, patientId, autoVerify }) {
    const updatedPatient = yield call(
      apiUpdatePatient,
      patientId,
      patientDetails,
      autoVerify,
    );
    yield put(updatePatient(updatedPatient));

    return updatedPatient;
  }

  * saga({ patientDetails, patientId, autoVerify, checkLoginStatus = true }) {
    const errorHandler = this.error;
    const loadingHandler = this.loading;
    const successHandler = this.success;

    const isLoading = yield select(loadingHandler.selector);

    if (isLoading) {
      return;
    }

    const currentUser = yield select(selectDataUser);
    const isPartnerUser = !!currentUser?.partner;
    let currentPatient;

    if (!isPartnerUser) {
      currentPatient = yield this.#validateCurrentPatient({ patientDetails, patientId, checkLoginStatus });
    }

    yield put(loadingHandler.update(true));
    yield put(errorHandler.update(null));
    yield put(successHandler.update(null));

    try {
      const targetPatientId = patientId || currentPatient?.id;
      let updatedPatient;

      if (isPartnerUser) {
        updatedPatient = yield this.#updatePartnerPatient({ patientDetails, patientId: targetPatientId, currentUser });
      } else {
        updatedPatient = yield this.#updateCurrentPatient({ patientDetails, patientId: targetPatientId, autoVerify });
      }

      yield put(successHandler.update(true));

      return updatedPatient;
    } catch (error) {
      sendToLogger(error, 'UpdatePatientDetails');
      yield put(errorHandler.update(extractErrorMessage(error)));
    } finally {
      yield put(loadingHandler.update(false));
    }
  }
}

export const UpdatePatientDetails = new _UpdatePatientDetails();
